// agentService.js
import { API } from '../api/API';
import useAuthStore from '../store/authStore';
import { extractRoleType } from '../utils/roleUtils';

let agentsCache = new Map();

const agentService = {
  
  // Récupérer tous les agents
  getAllAgents: async (page = 1, limit = 200, search = '', partenaire_id = '', role = '') => {
    try {
      const store = useAuthStore.getState();
      const params = { 
        page, 
        limit, 
        search,
        // Utiliser le partenaire_id passé en paramètre ou celui du store
        partenaire_id: partenaire_id || store.user?.partie_id
      };

      // Transformer le rôle avant de l'ajouter aux paramètres
      if (role) {
        params.role = extractRoleType(role);
      } else if (store.user?.role) {
        params.role = extractRoleType(store.user.role);
      }
      const response = await API.get('/agents', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des agents:', error);
      throw error;
    }
  },

  // Créer un nouvel agent
  createAgent: async (agentData) => {
    try {
      const formData = new FormData();
      
      // Ajouter l'image si elle existe
      if (agentData.image) {
        formData.append('file', agentData.image);
      }
      
      // Ajouter les autres données
      Object.keys(agentData).forEach(key => {
        if (key !== 'image') {
          // Convertir les booléens en string
          if (typeof agentData[key] === 'boolean') {
            formData.append(key, agentData[key].toString());
          } else if (agentData[key] !== null && agentData[key] !== undefined) {
            formData.append(key, agentData[key]);
          }
        }
      });

      // Pour le debug - afficher toutes les données envoyées
      console.log('Data being sent:', Object.fromEntries(formData.entries()));

      const response = await API.post('/agents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'agent:', error);
      throw error;
    }
  },

  // Modified getAgentById to handle both sync and async calls
  getAgentById: async (id) => {
    try {
      // Vérifier si l'agent est déjà dans le cache
      if (agentsCache.has(id)) {
        return agentsCache.get(id);
      }

      // Si l'agent n'est pas dans le cache, effectuer un appel API
      const response = await API.get(`/agents/${id}`);
      const agent = response.data;

      // Ajouter l'agent au cache pour les futurs appels
      agentsCache.set(id, agent);

      return agent;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'agent avec l'ID ${id}:`, error);
      throw error;
    }
  },

  // Add a method to preload agents
  preloadAgents: async () => {
    try {
      const response = await API.get('/agents');
      const agents = response.data.data || [];
      agents.forEach(agent => {
        agentsCache.set(agent.id, agent);
      });
      return agents;
    } catch (error) {
      console.error('Error preloading agents:', error);
      return [];
    }
  },

  // Mettre à jour un agent
  updateAgent: async (id, updateData) => {
    try {
      const response = await API.put(`/agents/${id}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'agent:', error);
      throw error;
    }
  },

  // Désactiver un agent
  deleteAgent: async (id) => {
    try {
      const response = await API.delete(`/agents/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la désactivation de l\'agent:', error);
      throw error;
    }
  },

  // Mettre à jour le statut d'un agent
  toggleAgentStatus: async (id) => {
    try {
      const response = await API.patch(`/agents/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      throw error;
    }
  },

  // Ajouter une méthode pour uploader l'image
  uploadImage: async (id, imageFile) => {
    try {
      const formData = new FormData();
      formData.append('file', imageFile); // Changé de 'image' à 'file'
      
      const response = await API.post(`/agents/${id}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'upload de l\'image:', error);
      throw error;
    }
  },
};

export default agentService;